import { computed, defineComponent, h } from 'vue'
import { QBtn, QIcon } from 'quasar'
import { ArcReviewSiteDto, BookDto, bsConstants, HasIndex, validStr } from 'booksprout'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'

const REVIEWS = bsConstants.ARCS.REVIEWS

/**
 * One retail site on a review row — Amazon, Audible, Kobo and the rest.
 *
 * Its state is either passed in as a flag or read off the review site itself, and `exempt` beats
 * everything: an exempt site is neither rejected nor delayed however it was marked.
 *
 * `needsSiteLink` is the "the author has not given us a link yet" case, and it is the ONE incomplete state
 * that is still clickable — it opens the add-link flow instead of the site. Amazon and Audible have their
 * own rules for it because each has two possible links.
 */
export default defineComponent({
  name: 'BsSiteBtn',

  /**
   * Vue 3 falls unrecognised attrs through to the root on its own, and that is wrong here: an `href`
   * reaching QBtn makes it render an anchor (Quasar 1's ignored it), so a rejected, delayed or exempt
   * site became clickable through to the store -- the one thing `shouldRenderLink` exists to prevent.
   * The render forwards `$attrs` itself, without the link attributes, when it is not a link.
   */
  inheritAttrs: false,

  props: {
    ...bsComponentProps,
    site: { type: Number, default: void 0 },
    reviewSite: { type: Object as () => ArcReviewSiteDto, default: void 0 },
    book: { type: Object as () => BookDto, default: void 0 },
    normal: { type: Boolean, default: false },
    delayed: { type: Boolean, default: false },
    rejected: { type: Boolean, default: false },
    exempt: { type: Boolean, default: false },
    userOwnsArc: { type: Boolean, default: false },
    newReviewOverride: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const vm = useVm()
    const bs = useBsComponent({ vm, props, emit })

    const isExempt = computed(() =>
      props.exempt || (props.reviewSite !== void 0 && props.reviewSite.exempt))

    const isRejected = computed(() => !isExempt.value &&
      (props.rejected ||
        (props.reviewSite !== void 0 && props.reviewSite.status === REVIEWS.SITE.STATUS.REJECTED)))

    const isDelayed = computed(() => !isExempt.value &&
      (props.delayed ||
        (props.reviewSite !== void 0 && props.reviewSite.status === REVIEWS.SITE.STATUS.DELAYED)))

    const isNormal = computed(() =>
      props.normal || (!isDelayed.value && !isRejected.value && !isExempt.value))

    const getIcon = computed(() => `app:${(REVIEWS.FLAG_ICONS as HasIndex)[props.site]}`)

    const needsSiteLink = computed(() => {
      const siteBookKey = (REVIEWS.FLAG_BOOK_KEYS_SITE as HasIndex)[props.site]

      // Amazon takes either an ebook ASIN or a print one, so both have to be missing.
      if (props.site === REVIEWS.FLAGS.AMAZON) {
        return props.book &&
          !validStr((props.book as HasIndex)[siteBookKey]) &&
          !validStr(props.book.linkAmazonAsinPrint) &&
          props.userOwnsArc
      }

      // Audible is US or UK, and — unlike the others — does not require the reader to own the arc.
      if (props.site === REVIEWS.FLAGS.AUDIBLE) {
        return props.book &&
          !validStr(props.book.linkAudibleUs) &&
          !validStr(props.book.linkAudibleUk)
      }

      return props.book && !validStr((props.book as HasIndex)[siteBookKey]) && props.userOwnsArc
    })

    const newReview = computed(() => props.userOwnsArc &&
      (props.newReviewOverride || props.reviewSite?.state === REVIEWS.SITE.STATE.NEW_LINK || false))

    const isIncomplete = computed(() => props.reviewSite === void 0 || needsSiteLink.value)

    const shouldRenderLink = computed(() =>
      vm.$attrs.href && !isRejected.value && !isDelayed.value && !isExempt.value)

    return {
      ...bs,
      isExempt,
      isRejected,
      isDelayed,
      isNormal,
      getIcon,
      needsSiteLink,
      newReview,
      isIncomplete,
      shouldRenderLink,
      getHasReviewAvailable: () => !!props.reviewSite.review
    }
  },

  methods: {
    // Every `__renderX`/`__emitClick` is renamed: Vue does not proxy keys beginning with `_`.
    renderIcon () {
      return h(QIcon, {
        class: 'font-size-20',
        name: this.getIcon
      })
    },

    renderText () {
      if (this.isNormal) {
        return void 0
      }

      const text = this.isDelayed
        ? 'Delayed'
        : (this.isExempt ? 'Exempt' : 'Rejected')

      return h('span', {
        class: 'on-right bs-tag-s'
      }, text)
    },

    renderBadge () {
      return h('span', {
        class: ['bs-sb-badge', {
          'bs-sb-badge--new': this.newReview,
          'bs-sb-badge--slr': this.needsSiteLink
        }]
      })
    },

    /**
     * One click, five possible meanings. An incomplete site with no link to add is inert; otherwise the
     * marked states each get their own event so the row above can explain them.
     */
    emitClick (e: Event) {
      if (this.isIncomplete && !this.needsSiteLink) {
        return
      }

      if (this.isRejected && !this.isNormal) {
        this.$emit('rejected-click', this.reviewSite)
      } else if (this.isDelayed && !this.isNormal) {
        this.$emit('delayed-click', this.reviewSite)
      } else if (this.isExempt && !this.isNormal) {
        this.$emit('exempt-click', this.reviewSite)
      } else if (this.needsSiteLink) {
        // don't open the search link
        e.preventDefault()
        this.$emit('add-site-link', e)
      } else {
        this.$emit('open-site-link', e)
      }
    }
  },

  render () {
    const dataCy = `bsSiteBtn_${(REVIEWS.FLAG_DESCRIPTIONS as HasIndex)[this.site].toLowerCase()}`

    const { href, target, rel, ...attrs } = this.$attrs as Record<string, any>

    const children = [
      this.renderBadge(),
      this.renderIcon(),
      this.renderText(),
      this.slot(this, 'default')
    ]

    /**
     * The props bag is cast: `h()` has a per-component overload, and with a UNION of a tag string
     * and QBtn TypeScript picks the component one and then wants every QBtn member.
     */
    return h(this.shouldRenderLink ? 'a' : QBtn as any, {
      ...attrs,
      // The caller's class is merged, not replaced: with `inheritAttrs: false` Vue no longer does it.
      class: [this.$attrs.class, 'bs-site-btn border-radius flex q-pa-sm items-center relative-position', this.getClasses({
        'bs-site-btn__rejected': this.isRejected && !this.isNormal,
        'bs-site-btn__delayed': this.isDelayed && !this.isNormal,
        'bs-site-btn__exempt': this.isExempt && !this.isNormal,
        // This will show the faded view for link required and no reviews available.
        'bs-site-btn__incomplete cursor-not-allowed': this.isIncomplete && !this.needsSiteLink,
        'cursor-inherit': !this.isNormal
      })],
      flat: true,
      onClick: this.emitClick,
      /**
       * The caller's own `data-cy` WINS, which is what Vue 2 did and no longer happens by itself.
       *
       * With `inheritAttrs` on, Vue 2 applied the parent's attributes over the root's own, so
       * `ArcSites`' `:data-cy="'site_' + site.site"` replaced this default. `inheritAttrs: false` here
       * (the render forwards `$attrs` itself, so nothing is applied twice) makes the order whatever
       * this bag says, and the default was overwriting the caller — taking 20-odd `site_N` selectors
       * in the e2e suite with it. Nothing selects on the default, but it stays for anything that
       * renders a site button without naming one.
       */
      ['data-cy']: attrs['data-cy'] ?? dataCy,
      ...(this.shouldRenderLink
        ? { href, target: target ?? '_blank', rel: rel ?? 'noopener noreferrer' }
        : {})
    // An element takes a children array; a component takes slot functions. This render is both,
    // depending on `shouldRenderLink`.
    }, this.shouldRenderLink ? children : { default: () => children })
  }
})
